import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {Observable, Subject} from 'rxjs';
import {
  ButtonConfig,
  SelectOption,
  User,
  CustomValidators,
  NotificationService,
  NotificationEnum,
  buttonsStaticConfig,
  inputErrorTypes, Company, DragAndDropError, UserSandbox
} from '../../../modules/global';
import {map} from 'rxjs/operators';
import { DialogContainerComponent } from '../../../modules/shared/components/dialog-container/dialog-container.component';
import { NgIf, AsyncPipe } from '@angular/common';
import { TextInputComponent } from '../../../modules/shared/components/inputs/text-input/text-input.component';
import { MatLabel } from '@angular/material/form-field';
import { SelectInputComponent } from '../../../modules/shared/components/inputs/select-input/select-input.component';
import { AmountInputComponent } from '../../../modules/shared/components/inputs/amount-input/amount-input.component';
import { TranslatePipe } from '@ngx-translate/core';

export interface ProfileDialogConfig {
  user: User;
  companies$: Observable<Array<Company>>;
  save: (any) => Observable<any>;
}

@Component({
    selector: 'slm-profile-dialog',
    templateUrl: './profile-dialog.component.html',
    styleUrls: ['./profile-dialog.component.scss'],
    imports: [DialogContainerComponent, NgIf, FormsModule, ReactiveFormsModule, TextInputComponent, MatLabel, SelectInputComponent, AmountInputComponent, AsyncPipe, TranslatePipe]
})
export class ProfileDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject<MatDialogRef<ProfileDialogComponent>>(MatDialogRef);
  data = inject<ProfileDialogConfig>(MAT_DIALOG_DATA);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly notificationService = inject(NotificationService);
  private userSandbox = inject(UserSandbox);

  public readonly buttons: Array<ButtonConfig> = [
    buttonsStaticConfig.cancel,
    {...buttonsStaticConfig.saveData}
  ];
  public readonly errors = [
    inputErrorTypes.required,
    inputErrorTypes.phone,
    inputErrorTypes.max,
    inputErrorTypes.min
  ];

  private readonly destroy$ = new Subject();
  public companySelect$: Observable<Array<SelectOption>>;

  public profileForm: UntypedFormGroup;

  ngOnInit() {
    this.companySelect$ = this.data.companies$.pipe(
      map(companies => companies
        .map(company => ({
          label: company.partnerName,
          value: +company?.partnerId
        } as SelectOption)))
    );
    this.profileForm = this.fb.group({
      userName: [
        `${this.data.user?.firstName || ''} ${this.data.user?.lastName || ''}`,
        [Validators.required]
      ],
      phone: [this.data.user?.phone || '', [CustomValidators.phone]],
      partnerId: [+this.data.user?.defaultPartnerId || '', [Validators.required]],
      completionDateBack: [+this.data.user?.completionDateBack, [Validators.required, Validators.min(7), Validators.max(365)]]
    });
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public buttonEvent(event: string) {
    if (event === 'save') {
      if (this.profileForm.invalid || this.buttons[1].isLoading) {
        this.profileForm.markAllAsTouched();
        return;
      }
      this.buttons[1].isLoading = true;
      this.dialogRef.disableClose = true;
      this.data.save(this.profileForm.value).subscribe({
        next: () => {
          this.dialogRef.close();
          this.userSandbox.reloadUser();
        },
        error: () => {
          this.buttons[1].isLoading = false;
          this.dialogRef.disableClose = false;
        }
      });
    } else {
      this.dialogRef.close();
    }
  }

  public uploadError(error: DragAndDropError): void {
    this.notificationService.notify(error.message, NotificationEnum.ERROR);
  }

  public fileSelected(_file): void {
  }
}
